import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Loader2, Star, Eye, EyeOff, Trash2, ShieldCheck, MessageSquare, ThumbsUp, Flag, FlagOff, Sparkles, CheckCircle2, Phone } from 'lucide-react';
import { toast } from 'sonner';
import { Input } from './ui/input';
import { adminAPI } from '../services/api';
import ReviewReplyBox from './ReviewReplyBox';
import ReviewPhotos from './ReviewPhotos';
import ReviewRewardSettings from './ReviewRewardSettings';
import ReviewAiSettings from './ReviewAiSettings';
import ManualReviewModal from './ManualReviewModal';
import WooReviewsImportModal from './WooReviewsImportModal';

const isPending = (r) => r.hidden && r.moderation?.status === 'pending';

const AdminReviewsPanel = () => {
  const [data, setData] = useState({ reviews: [], total: 0, hidden: 0, reported: 0, pending: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const initialQ = new URLSearchParams(useLocation().search).get('q') || '';
  const [filter, setFilter] = useState(initialQ);
  const [status, setStatus] = useState('all');
  const [busy, setBusy] = useState(null);
  const [manualOpen, setManualOpen] = useState(false);
  const [wooOpen, setWooOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setData(await adminAPI.getReviews());
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = async (review) => {
    setBusy(review.id);
    try {
      await adminAPI.setReviewVisibility(review.id, !review.hidden);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const approve = async (review) => {
    setBusy(review.id);
    try {
      const r = await adminAPI.approveReview(review.id);
      toast.success(r.reward?.code ? `${r.message} · code ${r.reward.code} offert au client` : r.message);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Validation impossible'); } finally {
      setBusy(null);
    }
  };

  const remove = async (review) => {
    if (!window.confirm(`Supprimer définitivement l'avis de ${review.user_name} ?`)) return;
    setBusy(review.id);
    try {
      await adminAPI.deleteReview(review.id);
      await load();
    } finally {
      setBusy(null);
    }
  };

  const visible = data.reviews.filter(r =>
    (status === 'all' || (status === 'reported' ? r.reports_count > 0 : status === 'pending' ? isPending(r) : status === 'hidden' ? r.hidden : !r.hidden)) &&
    (!filter || [r.product_name, r.user_name, r.comment, r.title, r.id].some(v => (v || '').toLowerCase().includes(filter.toLowerCase())))
  );

  const clearReports = async (review) => {
    if (!window.confirm(`Ignorer les ${review.reports_count} signalement(s) de cet avis ? Il restera publié.`)) return;
    setBusy(review.id);
    try {
      await adminAPI.clearReviewReports(review.id);
      await load();
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Avis clients ({data.total})</h1>
        <div className="flex items-center gap-2">
        <button type="button" onClick={() => setWooOpen(true)} data-testid="reviews-import-woo" className="inline-flex items-center gap-2 text-sm font-medium text-violet-800 bg-violet-100 hover:bg-violet-200 rounded-md px-3 py-2">Importer des avis WooCommerce</button>
        <button type="button" onClick={() => setManualOpen(true)} data-testid="reviews-add-manual" className="inline-flex items-center gap-2 text-sm font-medium text-white bg-[#1e3a5f] hover:bg-[#2d4a6f] rounded-md px-3 py-2"><Phone size={16} /> Saisir un avis reçu (téléphone / e-mail)</button>
        </div>
      </div>
      {wooOpen && <WooReviewsImportModal onClose={() => setWooOpen(false)} onDone={load} />}
      {manualOpen && <ManualReviewModal onClose={() => setManualOpen(false)} onCreated={load} />}
      <ReviewRewardSettings />
      <ReviewAiSettings />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        {[
          { icon: MessageSquare, label: 'Avis au total', value: data.total, testid: 'reviews-stat-total' },
          { icon: ShieldCheck, label: 'Publiés', value: data.total - data.hidden, testid: 'reviews-stat-visible' },
          { icon: EyeOff, label: 'Masqués', value: data.hidden, testid: 'reviews-stat-hidden' },
          { icon: Flag, label: 'Signalés (à vérifier)', value: data.reported || 0, testid: 'reviews-stat-reported', alert: (data.reported || 0) > 0 },
          { icon: Sparkles, label: 'En attente IA', value: data.pending || 0, testid: 'reviews-stat-pending', warn: (data.pending || 0) > 0 },
        ].map(s => (
          <div key={s.label} className={`bg-white rounded-lg shadow-sm p-4 flex items-center gap-4 ${s.alert ? 'ring-1 ring-red-200' : s.warn ? 'ring-1 ring-amber-200' : ''}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.alert ? 'bg-red-50 text-red-600' : s.warn ? 'bg-amber-50 text-amber-600' : 'bg-orange-50 text-orange-500'}`}><s.icon size={20} /></div>
            <div>
              <p className="text-xs text-gray-500">{s.label}</p>
              <p className="text-lg font-bold text-gray-900" data-testid={s.testid}>{s.value}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b flex flex-wrap items-center gap-3">
          <Input data-testid="reviews-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher (produit, client, texte)..." className="max-w-sm" />
          <div className="flex gap-1 ml-auto">
            {[['all', 'Tous'], ['visible', 'Publiés'], ['hidden', 'Masqués'], ['pending', `En attente IA${data.pending ? ` (${data.pending})` : ''}`], ['reported', `Signalés${data.reported ? ` (${data.reported})` : ''}`]].map(([v, l]) => (
              <button
                key={v}
                data-testid={`reviews-status-${v}`}
                onClick={() => setStatus(v)}
                className={`px-3 py-1.5 text-sm rounded-md ${status === v ? (v === 'reported' ? 'bg-red-600 text-white' : v === 'pending' ? 'bg-amber-500 text-white' : 'bg-orange-500 text-white') : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
              >
                {l}
              </button>
            ))}
          </div>
        </div>

        {isLoading ? (
          <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
        ) : visible.length === 0 ? (
          <p className="text-center py-10 text-gray-500" data-testid="reviews-admin-empty">Aucun avis</p>
        ) : (
          <ul className="divide-y" data-testid="reviews-admin-list">
            {visible.map(r => (
              <li key={r.id} data-testid={`admin-review-${r.id}`} className={`p-4 flex gap-4 ${isPending(r) ? 'bg-amber-50/50' : r.hidden ? 'bg-gray-50' : r.reports_count > 0 ? 'bg-red-50/40' : ''}`}>
                <img src={r.product_image} alt="" className="w-14 h-14 object-contain bg-white border rounded flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    <Link to={`/produit/${r.product_id}`} className="font-medium text-gray-900 hover:text-orange-600 text-sm">{r.product_name}</Link>
                    <span className="text-xs text-gray-400">{r.product_brand}</span>
                    <span className="flex items-center ml-2">
                      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={13} className={i <= r.rating ? 'fill-yellow-400 text-yellow-400' : 'text-gray-300'} />)}
                    </span>
                    <span
                      data-testid={`admin-review-status-${r.id}`}
                      className={`text-xs px-2 py-0.5 rounded-full font-medium ${r.hidden ? 'bg-gray-200 text-gray-600' : 'bg-green-100 text-green-700'}`}
                    >
                      {r.hidden ? 'Masqué' : 'Publié'}
                    </span>
                    {r.source === 'woocommerce' && <span data-testid={`admin-review-woo-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-violet-100 text-violet-800" title={`Importé de WooCommerce par ${r.collected_by || 'admin'}`}>Importé WooCommerce{r.verified ? ' · achat vérifié' : ''}</span>}
                    {r.source === 'merchant' && (
                      <span data-testid={`admin-review-merchant-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-sky-100 text-sky-800" title={`Saisi par ${r.collected_by || 'admin'}${r.order_number ? ` · commande ${r.order_number}` : ''}`}>
                        <Phone size={11} /> Recueilli par le marchand{r.channel ? ` · ${{ phone: 'téléphone', email: 'e-mail', store: 'comptoir', other: 'autre' }[r.channel] || r.channel}` : ''}{r.verified ? ' · achat vérifié' : ''}
                      </span>
                    )}
                    {isPending(r) && (
                      <span data-testid={`admin-review-pending-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-amber-100 text-amber-800" title={r.moderation?.reason}>
                        <Sparkles size={11} /> En attente IA{r.moderation?.reason ? ` · ${r.moderation.reason}` : ''}
                      </span>
                    )}
                    {r.reports_count > 0 && (
                      <span data-testid={`admin-review-reported-${r.id}`} className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full font-medium bg-red-100 text-red-700">
                        <Flag size={11} /> {r.reports_count} signalement{r.reports_count > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  {r.title && <p className="text-sm font-medium text-gray-800 mt-1">{r.title}</p>}
                  <p className="text-sm text-gray-600 mt-0.5 line-clamp-3">{r.comment}</p>
                  <ReviewPhotos photos={r.photos} testId={`admin-review-photos-${r.id}`} size="w-14 h-14" />
                  <p className="text-xs text-gray-400 mt-1">
                    {r.user_name} · {new Date(r.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}
                    {r.helpful_count > 0 && <span className="ml-2 inline-flex items-center gap-1 text-gray-600" data-testid={`admin-review-helpful-${r.id}`}><ThumbsUp size={11} /> {r.helpful_count} utile{r.helpful_count > 1 ? 's' : ''}</span>}
                  </p>
                  <ReviewReplyBox review={r} onChanged={load} />
                  {r.reports_count > 0 && (
                    <details className="mt-2 text-xs" data-testid={`admin-review-reports-${r.id}`}>
                      <summary className="cursor-pointer text-red-700 font-medium hover:underline">Voir les motifs de signalement</summary>
                      <ul className="mt-1.5 space-y-1 border-l-2 border-red-200 pl-3">
                        {r.reports.map((rep, i) => (
                          <li key={i} className="text-gray-700">
                            <span className="font-medium">{rep.reason_label}</span>
                            {rep.comment && <span className="text-gray-600"> — « {rep.comment} »</span>}
                            <span className="text-gray-400"> · {rep.user_email || 'visiteur anonyme'} · {new Date(rep.created_at).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}</span>
                          </li>
                        ))}
                      </ul>
                    </details>
                  )}
                </div>
                <div className="flex flex-col gap-1 flex-shrink-0">
                  {r.reports_count > 0 && (
                    <button
                      data-testid={`admin-review-clear-reports-${r.id}`}
                      onClick={() => clearReports(r)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded text-gray-600 hover:bg-gray-100 disabled:opacity-50"
                    >
                      <FlagOff size={14} /> Ignorer les signalements
                    </button>
                  )}
                  {isPending(r) && (
                    <button
                      data-testid={`admin-review-approve-${r.id}`}
                      onClick={() => approve(r)}
                      disabled={busy === r.id}
                      className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded font-medium bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
                    >
                      <CheckCircle2 size={14} /> Valider et publier
                    </button>
                  )}
                  <button
                    data-testid={`admin-review-toggle-${r.id}`}
                    onClick={() => toggle(r)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                  >
                    {r.hidden ? <Eye size={14} /> : <EyeOff size={14} />} {r.hidden ? 'Publier' : 'Masquer'}
                  </button>
                  <button
                    data-testid={`admin-review-delete-${r.id}`}
                    onClick={() => remove(r)}
                    disabled={busy === r.id}
                    className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded text-red-600 hover:bg-red-50 disabled:opacity-50"
                  >
                    <Trash2 size={14} /> Supprimer
                  </button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </>
  );
};

export default AdminReviewsPanel;
